/**
 * components/object/ObjectTimeline.jsx — Iter 47 unified operational
 * memory feed.
 *
 * Renders a chronological (newest first) list of every event tied to
 * an object: photos, notes, issues, status changes. Drop-in for any
 * object type — pass {objectType, objectId} and that's it.
 *
 * Per the directive: NO categorization, NO grouping, NO filters. The
 * timeline IS the source of truth.
 */
import React, { useEffect, useMemo, useState } from "react";
import {
  Camera, FileText, AlertCircle, CheckCircle2, RefreshCcw,
  Activity, Loader2,
} from "lucide-react";
import { fetchTimeline } from "@/lib/objectIntel";
import { formatRelative } from "@/lib/utils";

const KIND_META = {
  photo_added: { icon: Camera, color: "var(--fop-primary)", label: "Photo Added" },
  note_added: { icon: FileText, color: "var(--fop-text-2)", label: "Note Added" },
  issue_opened: { icon: AlertCircle, color: "var(--st-open, #f59e0b)", label: "Issue Opened" },
  issue_pending_verification: { icon: Activity, color: "var(--st-assigned, #6366f1)", label: "Issue → Pending Verification" },
  issue_closed: { icon: CheckCircle2, color: "var(--st-corrected, #10b981)", label: "Issue Closed" },
  issue_reopened: { icon: RefreshCcw, color: "var(--fop-warning, #f59e0b)", label: "Issue Reopened" },
  status_changed: { icon: Activity, color: "var(--fop-text-2)", label: "Status Changed" },
  object_deleted: { icon: AlertCircle, color: "var(--fop-danger, #ef4444)", label: "Object Deleted" },
};

export default function ObjectTimeline({ objectType, objectId, refreshKey = 0,
                                          emptyHint = "No activity yet. Add the first note or photo to start the record." }) {
  const [events, setEvents] = useState([]);
  const [loading, setLoading] = useState(true);
  const [err, setErr] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      setLoading(true);
      setErr(null);
      try {
        const evs = await fetchTimeline(objectType, objectId);
        if (!cancelled) setEvents(evs);
      } catch (e) {
        if (!cancelled) setErr(e?.message || "Failed to load timeline");
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [objectType, objectId, refreshKey]);

  const grouped = useMemo(() => events, [events]);

  if (loading) {
    return (
      <div className="flex items-center justify-center py-12 text-[var(--fop-text-3)]">
        <Loader2 size={16} className="animate-spin mr-2" />
        <span className="font-mono text-xs uppercase tracking-wider">Loading timeline…</span>
      </div>
    );
  }
  if (err) {
    return (
      <div className="fop-surface p-4 text-xs text-[var(--fop-danger)] font-mono" data-testid="object-timeline-error">
        {err}
      </div>
    );
  }
  if (!grouped.length) {
    return (
      <div className="text-center py-10 text-[var(--fop-text-3)] text-sm" data-testid="object-timeline-empty">
        {emptyHint}
      </div>
    );
  }

  return (
    <ol className="space-y-2" data-testid="object-timeline-list">
      {grouped.map((e) => (
        <TimelineRow key={`${e.kind}:${e.id}`} ev={e} />
      ))}
    </ol>
  );
}

function TimelineRow({ ev }) {
  const meta = KIND_META[ev.kind] || KIND_META.status_changed;
  const Icon = meta.icon;
  return (
    <li
      className="flex gap-3 fop-surface px-3 py-2 rounded-md hover:bg-[var(--fop-surface-3)] transition-colors"
      data-testid={`timeline-row-${ev.kind}`}
    >
      <div
        className="w-7 h-7 rounded-full flex items-center justify-center shrink-0 mt-0.5"
        style={{ backgroundColor: `${meta.color}22`, color: meta.color }}
      >
        <Icon size={14} />
      </div>
      <div className="flex-1 min-w-0">
        <div className="flex items-baseline gap-2">
          <span className="text-sm text-[var(--fop-text)] font-medium leading-tight">
            {ev.summary || meta.label}
          </span>
          <span className="text-[11px] font-mono uppercase tracking-wider text-[var(--fop-text-4)] ml-auto whitespace-nowrap">
            {formatRelative(ev.created_at)}
          </span>
        </div>
        {ev.detail && (
          <p className="text-xs text-[var(--fop-text-2)] mt-0.5 leading-snug line-clamp-3 whitespace-pre-wrap break-words">
            {ev.detail}
          </p>
        )}
        {ev.actor_name && (
          <p className="text-[10px] text-[var(--fop-text-4)] font-mono uppercase tracking-wider mt-0.5">
            {ev.actor_name}
          </p>
        )}
      </div>
    </li>
  );
}
